/**
 * "Baxter just changed X" — a one-line bus between the chat panel and whatever
 * route is on screen behind it.
 *
 * The panel invalidates the query keys itself (that's what re-renders the data);
 * this store only carries the SIGNAL, so a list can flash the rows that moved and
 * an editor can decide between reloading silently and offering a Reload bar.
 */

import { create } from 'zustand';
import { noticeFor, type Domain, type MediaTab } from '@/components/baxter/toolEffects';

/**
 * A counter, not a clock: the value only ever has to CHANGE, and two tool
 * results inside one millisecond would collide on Date.now(). Also keeps every
 * consumer clock-skew-proof — nothing here is ever compared to a server time.
 */
let tick = 0;

export interface SidebarNotice { id: number; text: string; createdAt: number; expiresAt: number }
export const NOTICE_MS = 4500;

interface BaxterEffectsState {
  changed: Partial<Record<Domain, { at: number; id?: string; settled?: boolean }>>;
  /** Non-null while a turn is streaming. */
  turnStartedAt: number | null;
  noticeSiteSlug: string | null;
  viewedDomain: Domain | null;
  notices: Partial<Record<Domain, SidebarNotice[]>>;
  announcement: { at: number; text: string } | null;
  mediaTabs: Partial<Record<MediaTab, number>>;
  acknowledgeMediaTab: (siteSlug: string, tab: MediaTab, at: number) => void;
  visit: (siteSlug: string, domain: Domain | null) => void;
  mark: (domains: Domain[], id?: string, notice?: {
    siteSlug: string;
    call: { name: string; args?: unknown; previousData?: unknown };
    text?: string;
    mediaTabs?: MediaTab[];
  }) => number;
  settle: (at: number) => void;
  expireNotices: (now: number) => void;
  startTurn: () => number;
  endTurn: (at?: number) => void;
}

export const useBaxterEffects = create<BaxterEffectsState>((set) => ({
  changed: {},
  turnStartedAt: null,
  noticeSiteSlug: null,
  viewedDomain: null,
  notices: {},
  announcement: null,
  mediaTabs: {},
  acknowledgeMediaTab: (siteSlug, tab, at) => set((s) => {
    if (s.noticeSiteSlug !== siteSlug || s.mediaTabs[tab] !== at) return s;
    const mediaTabs = { ...s.mediaTabs };
    delete mediaTabs[tab];
    return { mediaTabs };
  }),
  visit: (siteSlug, domain) => set((s) => {
    const sameSite = s.noticeSiteSlug === siteSlug;
    if (sameSite && s.viewedDomain === domain && (!domain || !s.notices[domain])) return s;
    const notices = sameSite ? { ...s.notices } : {};
    if (domain) delete notices[domain];
    return {
      noticeSiteSlug: siteSlug, viewedDomain: domain, notices,
      mediaTabs: sameSite ? s.mediaTabs : {},
      announcement: sameSite ? s.announcement : null,
    };
  }),
  mark: (domains, id, notice) => {
    const at = ++tick;
    set((s) => {
      if (notice && notice.siteSlug !== s.noticeSiteSlug) return s;
      const changed = { ...s.changed };
      for (const d of domains) changed[d] = { at, ...(id ? { id } : {}) };
      const notices = { ...s.notices };
      const received: string[] = [];
      if (notice && notice.siteSlug === s.noticeSiteSlug) {
        for (const domain of domains) {
          if (domain === s.viewedDomain || domain === 'site') continue;
          const text = notice.text ?? noticeFor(notice.call, domain, notice.siteSlug);
          if (text) {
            const queue = notices[domain] ?? [];
            notices[domain] = [...queue, { id: ++tick, text, createdAt: Date.now(), expiresAt: Math.max(Date.now() + NOTICE_MS, (queue.at(-1)?.expiresAt ?? 0) + 250) }];
            received.push(text);
          }
        }
      }
      return {
        changed, notices,
        mediaTabs: notice?.mediaTabs?.length ? { ...s.mediaTabs, ...Object.fromEntries(notice.mediaTabs.map((tab) => [tab, at])) } : s.mediaTabs,
        announcement: received.length ? { at, text: [...new Set(received)].join('. ') } : s.announcement,
      };
    });
    return at;
  },
  settle: (at) => set((s) => ({ changed: Object.fromEntries(Object.entries(s.changed)
    .map(([domain, change]) => [domain, change.at === at ? { ...change, settled: true } : change])) })),
  expireNotices: (now) => set((s) => ({ notices: Object.fromEntries(
    Object.entries(s.notices).map(([domain, queue]) => [domain, queue.filter((n) => n.expiresAt > now)])
      .filter(([, queue]) => (queue as SidebarNotice[]).length),
  ) })),
  // A new turn owns the highlights: whatever the last one flashed is history.
  startTurn: () => { const at = ++tick; set({ changed: {}, turnStartedAt: at }); return at; },
  endTurn: (at) => set((s) => at !== undefined && at !== s.turnStartedAt ? s : { turnStartedAt: null }),
}));

/**
 * The last change to `domain`, as a value that only increases — `null` when
 * nothing has changed this turn. Pass `id` from an editor: a call that named a
 * DIFFERENT row is not this editor's business, while a call that named no row
 * (a whole-domain change) still counts.
 */
export function useBaxterChanged(domain: Domain, id?: string): number | null {
  return useBaxterEffects((s) => changedAt(s.changed, domain, id));
}

/** The selector above, as a plain function so the three branches are testable. */
export function changedAt(
  changed: BaxterEffectsState['changed'],
  domain: Domain,
  id?: string,
): number | null {
  const hit = changed[domain];
  if (!hit) return null;
  if (id && hit.id && hit.id !== id) return null;
  return hit.at;
}

/** Route descendants (including editors) belong to the same sidebar section. */
export function sidebarDomain(pathname: string, siteSlug: string): Domain | null {
  const section = pathname.startsWith(`/sites/${siteSlug}/`)
    ? pathname.slice(`/sites/${siteSlug}/`.length).split('/')[0] : null;
  return section && ['pages', 'posts', 'content-types', 'media', 'theme', 'forms',
    'bark', 'members', 'platforms', 'settings'].includes(section) ? section as Domain : null;
}


/** Keep mobile notices beside the text when the full drawer leaves too little room. */
export function sidebarNoticePosition(
  item: { top: number; right: number }, labelRight: number,
  viewport: { width: number; height: number }, stackHeight: number,
): { top: number; left: number; width: number } {
  const edge = viewport.width - item.right - 20 >= 120 ? item.right : labelRight;
  const left = Math.max(12, edge + 8);
  return {
    left,
    width: Math.max(0, Math.min(272, viewport.width - left - 12)),
    top: Math.max(12, Math.min(item.top, viewport.height - stackHeight - 12)),
  };
}


/** One exterior outline; no interior seam between the nav item and its stack. */
export function sidebarNoticeOutline(
  item: { left: number; top: number; bottom: number },
  notice: { left: number; top: number; width: number; height: number },
): string {
  const right = notice.left + notice.width;
  const bottom = notice.top + notice.height;
  const corners = [[item.left, item.top], [notice.left, item.top], [notice.left, notice.top],
    [right, notice.top], [right, bottom], [notice.left, bottom], [notice.left, item.bottom], [item.left, item.bottom]];
  const distinct = corners.filter((p, i) => !i || p[0] !== corners[i - 1][0] || p[1] !== corners[i - 1][1]);
  const points = distinct.filter((p, i) => {
    const previous = distinct[(i + distinct.length - 1) % distinct.length];
    const next = distinct[(i + 1) % distinct.length];
    return (p[0] - previous[0]) * (next[1] - p[1]) !== (p[1] - previous[1]) * (next[0] - p[0]);
  });
  return points.map((point, i) => {
    const previous = points[(i + points.length - 1) % points.length];
    const next = points[(i + 1) % points.length];
    const beforeLength = Math.hypot(previous[0] - point[0], previous[1] - point[1]);
    const afterLength = Math.hypot(next[0] - point[0], next[1] - point[1]);
    const radius = Math.min(6, beforeLength / 2, afterLength / 2);
    const before = point.map((v, axis) => v + (previous[axis] - v) * radius / beforeLength);
    const after = point.map((v, axis) => v + (next[axis] - v) * radius / afterLength);
    return `${i ? 'L' : 'M'}${before.join(' ')}Q${point.join(' ')} ${after.join(' ')}`;
  }).join('') + 'Z';
}
